'use client'
import { AcceptPopup } from '@/components/AcceptPopup'
import { Button } from '@/components/Button'
import { Icon } from '@/icons'
import { MktuClassWord } from '@/services/Api/generated'
import { mktuDataStore } from '@/domains/Site/store/MktuData.store'
import cn from 'classnames'
import { FunctionComponent, useCallback, useMemo, useState } from 'react'
import { Word } from './components/Word'
import styles from './MktuCard.module.scss'

const MIN_VISIBLE_WORDS = 10

type MktuCardProps = {
  className?: string
  number: number
  words: MktuClassWord[]
  onShowDetails?: (classNumber: number) => void
  onRemove?: (classNumber: number) => void
  onRemoveWord?: (classNumber: number, word: MktuClassWord) => void
}

export const MktuCard: FunctionComponent<MktuCardProps> = ({
  className,
  number,
  words,
  onShowDetails,
  onRemove,
  onRemoveWord,
}) => {
  const [visibleAll, setVisibleAll] = useState(false)
  const [showDeletePopup, setShowDeletePopup] = useState(false)

  const removeWord = useCallback(
    (word: MktuClassWord) => {
      onRemoveWord?.(number, word)
    },
    [number, onRemoveWord]
  )

  const toggle = useCallback(() => {
    setVisibleAll((v) => !v)
  }, [])

  const visibleWords = useMemo(() => {
    return visibleAll ? words : words.slice(0, MIN_VISIBLE_WORDS)
  }, [visibleAll, words])

  const showDetails = useCallback(() => {
    onShowDetails?.(number)
  }, [number, onShowDetails])

  const openDeletePopup = useCallback(() => {
    setShowDeletePopup(true)
  }, [])

  const hideDeletePopup = useCallback(
    (confirmed: boolean) => {
      setShowDeletePopup(false)
      if (confirmed) {
        mktuDataStore.removeSelectedClass(number)
        if (onRemove) {
          onRemove(number)
        }
      }
    },
    [number, onRemove]
  )

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.name}>Класс {number}</div>
      <div className={styles.controlButton}>
        <Button
          variant="ghost"
          size="small"
          onlyIcon
          onClick={openDeletePopup}
          ariaLabel="Удалить класс"
          testId="ButtonDeleteMktuClass"
        >
          <Icon name="trash" className={styles.trashIcon} />
        </Button>
      </div>
      <div className={styles.positionTitle}>Выбранные позиции</div>
      <div className={styles.positionsList}>
        {visibleWords.map((word) => (
          <Word
            key={word.uuid}
            word={word}
            onRemoveWord={onRemoveWord != null ? removeWord : undefined}
          />
        ))}
        {words.length > MIN_VISIBLE_WORDS ? (
          <button type="button" className={styles.button} onClick={toggle}>
            {visibleAll ? 'Свернуть' : `+${words.length - MIN_VISIBLE_WORDS}`}
          </button>
        ) : null}
      </div>
      {onShowDetails ? (
        <Button
          onClick={showDetails}
          className={styles.more}
          variant="ghost"
          size="small"
        >
          <Icon name="pencil" className={styles.pencilIcon} />
          Редактировать
        </Button>
      ) : (
        <Button
          href={`/mktu/classes/${number}`}
          className={styles.more}
          variant="ghost"
          size="small"
        >
          <Icon name="pencil" className={styles.pencilIcon} />
          Редактировать
        </Button>
      )}
      {showDeletePopup ? (
        <AcceptPopup
          onClose={hideDeletePopup}
          data={{
            title: 'Вы уверены?',
            description:
              'Вы действительно хотите удалить класс? Вернуть изменения обратно невозможно',
            cancelButton: 'Отмена',
            acceptButton: 'Удалить',
          }}
        />
      ) : null}
    </div>
  )
}
